import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { PrivateImageLibraryClient } from './api/client';
import type {
  PrivateImageLibraryFilter,
  PrivateImageLibraryPage,
  PrivateImageLibraryItem,
  PrivateImageLibraryResponse
} from './types';

/**
 * Main hook for private image library with infinite scroll and search
 * Uses PostgreSQL function for optimized performance
 */
export function usePrivateImageLibrary(
  workbookId: string,
  filters?: PrivateImageLibraryFilter
) {
  const { user } = useUser();
  const limit = filters?.limit || 12;

  return useInfiniteQuery({
    queryKey: ['private-image-library', workbookId, filters],
    queryFn: async ({ pageParam = 0 }): Promise<PrivateImageLibraryPage> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const offset = pageParam * limit;
      const rawData = await PrivateImageLibraryClient.fetchImages(
        workbookId,
        user.id,
        filters?.searchQuery,
        limit,
        offset
      );

      // Transform the PostgreSQL function response to our format
      const transformedData: PrivateImageLibraryItem[] = rawData.map((item: PrivateImageLibraryResponse) => ({
        image_id: item.image_id,
        image_url: item.image_url,
        filename: item.filename,
        alt_text: item.alt_text,
        generation_prompt: item.generation_prompt,
        width: item.width,
        height: item.height,
        file_size: item.file_size,
        is_generated: item.is_generated,
        created_at: item.created_at,
        workbook_id: item.workbook_id, // ADDED: Include workbook_id from SQL function response
      }));

      // Get pagination metadata from first item (all items have same metadata)
      const totalCount = rawData.length > 0 ? rawData[0].total_count : 0;
      const hasMore = rawData.length > 0 ? rawData[0].has_more : false;

      return {
        data: transformedData,
        hasMore,
        total: totalCount,
        page: pageParam
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!workbookId && !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to get quick image count for a workbook
 */
export function usePrivateImageLibraryCount(workbookId: string) {
  const { user } = useUser();

  return useQuery({
    queryKey: ['private-image-library-count', workbookId],
    queryFn: async (): Promise<number> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      return await PrivateImageLibraryClient.getImageCount(workbookId, user.id);
    },
    enabled: !!workbookId && !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
} 